import type { Action, CurrentUser } from '@/auth/permissions'
import { can } from '@/auth/permissions'

/**
 * Структура навигации и её фильтрация по правам (ТЗ §5.1).
 *
 * Меню описано здесь, а не приходит с сервера: состав навигации — вопрос интерфейса, и
 * новый экран не должен требовать правки Java. Сервер отдаёт права, клиент решает, что из
 * них показать.
 */

/** Пункт меню. */
export interface NavItem {
  /** Путь роутера; он же ключ для горячих клавиш и палитры команд. */
  path: string
  /** Ключ локализации, а не готовая строка: интерфейс двуязычный (ТЗ §5.1). */
  labelKey: string
  /** Ресурс и действие, без которых пункт не показывается. */
  resource: string
  action: Action
  /** Иконка — имя из набора; рисуется компонентом. */
  icon: string
  /**
   * Код сущности в каталоге списков сервера (`GET /api/v1/lists`).
   *
   * Есть не у всех пунктов: настраиваемый список существует только там, где сервер
   * объявил сущность в реестре (#48). Пункт без него открывает заглушку — и это честнее,
   * чем открыть пустую таблицу, которую нечем наполнить.
   */
  listEntity?: string
  /**
   * Собственный экран вместо настраиваемого списка.
   *
   * Есть у разделов, которые списком не являются по существу: конструктор отчётов строит
   * запрос, а не листает строки, а готовый отчёт — это формула (конверсия по истории
   * стадий, ABC по накопленной доле в обороте), которую фильтрами списка не выразить.
   *
   * Завести им сущность в каталоге списков значило бы пообещать таблицу, за которой
   * ничего нет, — то есть ровно ту заглушку, от которой уходили в #69. Пункт без
   * `listEntity` и без `screen` — по-прежнему заглушка, и это по-прежнему проверяется.
   */
  screen?:
    | 'my-day'
    | 'dashboards'
    | 'report-builder'
    | 'sales-reports'
    | 'finance-reports'
    | 'warehouse-reports'
    | 'warehouse-topology'
    | 'product-categories'
    | 'price-lists'
    | 'sales-plans'
    | 'forecast-model'
    | 'lead-score-model'
    | 'support-insights'
    | 'client-risk'
    | 'admin-support-routing'
    | 'report-schedules'
    | 'reconciliation'
    | 'cash-book'
    | 'fiscal'
    | 'approvals'
    | 'tasks'
    | 'calendar'
    | 'notifications'
    | 'mail-inbox'
    | 'messenger-inbox'
    | 'admin-numbering'
    | 'admin-thresholds'
    | 'admin-currencies'
    | 'admin-tax-rates'
    | 'admin-units'
    | 'admin-deal-reasons'
    | 'admin-funnels'
    | 'admin-lead-rules'
    | 'admin-automation'
    | 'admin-audit'
    | 'admin-tags'
    | 'admin-custom-fields'
    | 'admin-users'
    | 'admin-roles'
    | 'admin-settings'
    | 'admin-mail'
    | 'admin-ai'
    | 'admin-telephony'
    | 'admin-messengers'
    | 'admin-integrations'
    | 'assistant'
    | 'subscriptions'
  /** Вложенные пункты. */
  children?: NavItem[]
  /**
   * Пункт имеет смысл только для учётной записи с карточкой сотрудника.
   *
   * «Мой день» собирает **мои** задачи, сделки и клиентов, а «моё» считается по
   * сотруднику: у внешнего клиента и служебной записи его нет по устройству, и экран
   * встретил бы их пустотой, которая читается как «всё сделано».
   */
  requiresEmployee?: boolean
}

/** Раздел меню: заголовок и пункты под ним. */
export interface NavSection {
  titleKey: string
  items: NavItem[]
}

/**
 * «Мой день» — пункт над группами меню, как в макете (Sidebar.dc.html): это не раздел
 * учёта, а стартовый экран человека, и прятать его внутрь группы значило бы заставлять
 * раскрывать «Работу», чтобы начать день.
 *
 * Право — `sales.activity:read`: задачи и встречи, ради которых экран открывают, читаются
 * по нему. Остальные блоки экрана проверяют свои права сами и без них говорят «нет
 * доступа», а не исчезают.
 */
export const MY_DAY: NavItem = {
  path: '/my-day',
  labelKey: 'nav.myDay',
  resource: 'sales.activity',
  action: 'read',
  icon: 'sun',
  screen: 'my-day',
  requiresEmployee: true,
}

/** Пункты над группами меню. */
export const TOP_ITEMS: NavItem[] = [MY_DAY]

/** Доступен ли пункт: право на ресурс и, где нужно, карточка сотрудника. */
function allowed(user: CurrentUser | null, item: NavItem): boolean {
  if (item.requiresEmployee && !user?.employeeId) return false
  return can(user, item.resource, item.action)
}

/** Доступные пункты над группами. */
export function visibleTopItems(user: CurrentUser | null): NavItem[] {
  return TOP_ITEMS.filter((item) => allowed(user, item))
}

/**
 * Полная навигация приложения.
 *
 * Ресурсы совпадают с теми, что заведены в `crm.permission` (V7). Опечатка здесь означает
 * пункт, который не покажется никому, — поэтому имена берутся из справочника, а не
 * придумываются.
 */
export const NAVIGATION: NavSection[] = [
  {
    titleKey: 'nav.section.sales',
    items: [
      { path: '/leads', labelKey: 'nav.leads', resource: 'sales.lead', action: 'read', icon: 'target', listEntity: 'sales.lead' },
      { path: '/deals', labelKey: 'nav.deals', resource: 'sales.deal', action: 'read', icon: 'handshake', listEntity: 'sales.deal' },
      { path: '/counterparties', labelKey: 'nav.counterparties', resource: 'sales.counterparty', action: 'read', icon: 'building', listEntity: 'sales.counterparty' },
      // Контактные лица (#98): люди контрагентов. «Свои» — люди своих клиентов: владелец
      // контакта считается через ответственного за контрагента, а не отдельной колонкой.
      { path: '/contacts', labelKey: 'nav.contacts', resource: 'sales.contact', action: 'read', icon: 'contact', listEntity: 'sales.contact' },
      // Обращения клиентов (#123): очередь менеджера — список механизма списков (фильтры по
      // статусу, исполнителю, просрочке SLA), карточка — переписка, заметки, назначение.
      { path: '/support', labelKey: 'nav.support', resource: 'support.ticket', action: 'read', icon: 'comment', listEntity: 'support.ticket' },
      // Сводка по обращениям (#132): темы — категории разбора ИИ, всплески — рост категории к
      // прошлому периоду; только обращения в области смотрящего, поэтому то же право, что у очереди.
      { path: '/support-insights', labelKey: 'nav.supportInsights', resource: 'support.ticket', action: 'read', icon: 'chart-bar', screen: 'support-insights' },
      { path: '/quotes', labelKey: 'nav.quotes', resource: 'sales.quote', action: 'read', icon: 'file-text', listEntity: 'sales.quote' },
      { path: '/products', labelKey: 'nav.products', resource: 'sales.product', action: 'read', icon: 'package', listEntity: 'sales.product' },
      // Дерево категорий (V60): структура каталога правится правами на товары, а не
      // администрированием — её ведёт тот же человек, что заводит товары.
      { path: '/product-categories', labelKey: 'nav.productCategories', resource: 'sales.product', action: 'read', icon: 'folder-tree', screen: 'product-categories' },
      // Прайс-листы: справочник прайсов и страница цен прайса — свой экран, а не список:
      // цены листаются внутри прайса, а правила (одно умолчание на валюту) держит сервер.
      { path: '/price-lists', labelKey: 'nav.priceLists', resource: 'sales.pricelist', action: 'read', icon: 'tag', screen: 'price-lists' },
      // Планы продаж (#119): план-факт за квартал по менеджерам и отделам с прогнозом. Свой
      // экран, а не список: строка — это план против факта, посчитанного из сделок.
      { path: '/sales-plans', labelKey: 'nav.salesPlans', resource: 'sales.plan', action: 'read', icon: 'chart-line', screen: 'sales-plans' },
      // Прогноз сделок (#128): качество модели на отложенной выборке рядом с базовой линией
      // «вероятность стадии» и обучение заново. Сам прогноз — на доске и в карточке сделки.
      { path: '/forecast-model', labelKey: 'nav.forecastModel', resource: 'sales.forecast', action: 'read', icon: 'chart-bar', screen: 'forecast-model' },
      // Оценка лидов (#129): качество модели на отложенной выборке рядом с правилом баллов и
      // обучение заново по исходам. Сама оценка — на карточке и в списке лидов.
      { path: '/lead-score-model', labelKey: 'nav.leadScoreModel', resource: 'sales.leadscore', action: 'read', icon: 'flag', screen: 'lead-score-model' },
      // Клиенты в зоне риска (#135): правило по платежам, переписке и жалобам, причины словами.
      // Своё право sales.risk (решение владельца); строки — пересечение с областью клиентов.
      { path: '/client-risk', labelKey: 'nav.clientRisk', resource: 'sales.risk', action: 'read', icon: 'alert', screen: 'client-risk' },
    ],
  },
  {
    titleKey: 'nav.section.billing',
    items: [
      { path: '/invoices', labelKey: 'nav.invoices', resource: 'billing.invoice', action: 'read', icon: 'receipt', listEntity: 'billing.invoice' },
      { path: '/documents', labelKey: 'nav.documents', resource: 'billing.document', action: 'read', icon: 'file-check', listEntity: 'billing.document' },
      { path: '/contracts', labelKey: 'nav.contracts', resource: 'billing.contract', action: 'read', icon: 'file-signature', listEntity: 'billing.contract' },
      // Подписки (#94): список `billing.recurring` под правом счетов — свой экран, а не
      // `listEntity`: ключ сущности здесь намеренно не совпадает с ресурсом права, а
      // «listEntity = resource» — инвариант списочных пунктов.
      { path: '/subscriptions', labelKey: 'nav.subscriptions', resource: 'billing.invoice', action: 'read', icon: 'refresh', screen: 'subscriptions' },
    ],
  },
  {
    titleKey: 'nav.section.warehouse',
    items: [
      { path: '/stock', labelKey: 'nav.stock', resource: 'warehouse.stock', action: 'read', icon: 'boxes', listEntity: 'warehouse.stock' },
      { path: '/receipts', labelKey: 'nav.receipts', resource: 'warehouse.receipt', action: 'read', icon: 'truck-in', listEntity: 'warehouse.receipt' },
      { path: '/shipments', labelKey: 'nav.shipments', resource: 'warehouse.shipment', action: 'read', icon: 'truck-out', listEntity: 'warehouse.shipment' },
      { path: '/picking', labelKey: 'nav.picking', resource: 'warehouse.pick', action: 'read', icon: 'clipboard', listEntity: 'warehouse.pick' },
      { path: '/stocktakes', labelKey: 'nav.stocktakes', resource: 'warehouse.stocktake', action: 'read', icon: 'scale', listEntity: 'warehouse.stocktake' },
      { path: '/transfers', labelKey: 'nav.transfers', resource: 'warehouse.transfer', action: 'read', icon: 'arrow-right-left', listEntity: 'warehouse.transfer' },
      // Склады и топология: справочник складов, зоны хранения, ячейки и генератор ячеек.
      // Свой экран, а не список: у склада зоны, у зоны ячейки, и правка идёт по уровням —
      // таблица складов этого не выражает. Последним в разделе: стартовый экран кладовщика —
      // остатки, а не справочник.
      { path: '/warehouses', labelKey: 'nav.warehouses', resource: 'warehouse.topology', action: 'read', icon: 'building', screen: 'warehouse-topology' },
    ],
  },
  {
    titleKey: 'nav.section.hr',
    items: [
      { path: '/employees', labelKey: 'nav.employees', resource: 'hr.employee', action: 'read', icon: 'users', listEntity: 'hr.employee' },
      { path: '/departments', labelKey: 'nav.departments', resource: 'hr.department', action: 'read', icon: 'sitemap', listEntity: 'hr.department' },
      { path: '/vacancies', labelKey: 'nav.vacancies', resource: 'hr.vacancy', action: 'read', icon: 'user-plus', listEntity: 'hr.vacancy' },
      { path: '/leaves', labelKey: 'nav.leaves', resource: 'hr.leave', action: 'read', icon: 'calendar', listEntity: 'hr.leave' },
      { path: '/kpi', labelKey: 'nav.kpi', resource: 'hr.kpi', action: 'read', icon: 'gauge', listEntity: 'hr.kpi' },
    ],
  },
  {
    // Аналитика (#76). Все пункты — собственные экраны: отчёт это формула, а не выборка,
    // и настраиваемым списком он не выражается. Право одно на весь раздел
    // (`analytics.report`), но данные в каждом отчёте закрыты правом на свой источник —
    // проверяется на сервере, от имени запускающего.
    titleKey: 'nav.section.analytics',
    items: [
      // Дашборды идут первыми в разделе намеренно: это точка входа руководителя
      // (#77), а не ещё один отчёт. Право своё — `analytics.dashboard`, а не
      // `analytics.report`: дашборды можно открыть тому, кому конструктор не нужен.
      { path: '/dashboards', labelKey: 'nav.dashboards', resource: 'analytics.dashboard', action: 'read', icon: 'layout-dashboard', screen: 'dashboards' },
      { path: '/report-builder', labelKey: 'nav.reportBuilder', resource: 'analytics.report', action: 'read', icon: 'wand', screen: 'report-builder' },
      { path: '/sales-reports', labelKey: 'nav.salesReports', resource: 'analytics.report', action: 'read', icon: 'chart-line', screen: 'sales-reports' },
      { path: '/finance-reports', labelKey: 'nav.financeReports', resource: 'analytics.report', action: 'read', icon: 'coins', screen: 'finance-reports' },
      { path: '/warehouse-reports', labelKey: 'nav.warehouseReports', resource: 'analytics.report', action: 'read', icon: 'chart-bar', screen: 'warehouse-reports' },
      { path: '/report-schedules', labelKey: 'nav.reportSchedules', resource: 'analytics.report', action: 'read', icon: 'clock', screen: 'report-schedules' },
    ],
  },
  {
    // Оплата (#78). Раздела ТЗ §4.4 не было в меню вовсе, хотя логика была написана и
    // покрыта тестами: платежи в crm-billing, фискализация в crm-payments.
    //
    // Три права, и они не взаимозаменяемы. Реестр платежей и чеки — это списки
    // (`payments.payment`), поэтому у них `listEntity`. Сверка, касса и фискализация —
    // собственные экраны: сверка это рабочий процесс из трёх шагов, касса — журнал со
    // сторнированием, а список долгов перед ОФД — не выборка, а вопрос «что не уехало».
    titleKey: 'nav.section.payments',
    items: [
      { path: '/payments', labelKey: 'nav.paymentRegistry', resource: 'payments.payment', action: 'read', icon: 'wallet', listEntity: 'payments.payment' },
      { path: '/reconciliation', labelKey: 'nav.reconciliation', resource: 'payments.statement', action: 'read', icon: 'file-import', screen: 'reconciliation' },
      { path: '/cash-book', labelKey: 'nav.cashBook', resource: 'payments.ledger', action: 'read', icon: 'book', screen: 'cash-book' },
      { path: '/fiscal', labelKey: 'nav.fiscal', resource: 'payments.payment', action: 'read', icon: 'printer', screen: 'fiscal' },
    ],
  },

  {
    // Сквозные механизмы (#79). Своя секция, а не пункты внутри продаж: согласование
    // счёта, задача кладовщика и упоминание в комментарии не принадлежат ни одному
    // разделу — они про работу самого человека, а не про сущность, с которой он работает.
    //
    // Все три — собственные экраны: очередь на подпись это не выборка со сдвинутым
    // фильтром, у неё маршрут, шаги и решения, и настраиваемым списком она выражается в
    // таблицу, которая отвечает не на тот вопрос.
    titleKey: 'nav.section.work',
    items: [
      { path: '/approvals', labelKey: 'nav.approvals', resource: 'system.approval', action: 'read', icon: 'stamp', screen: 'approvals' },
      { path: '/tasks', labelKey: 'nav.tasks', resource: 'sales.activity', action: 'read', icon: 'check-square', screen: 'tasks' },
      // Календарь (#113): свои задачи и встречи и — в области права — коллег. Без карточки
      // сотрудника не прячется: руководитель со служебной записью и scope `all` открывает
      // календари отдела, своего у него просто нет.
      { path: '/calendar', labelKey: 'nav.calendar', resource: 'sales.activity', action: 'read', icon: 'calendar', screen: 'calendar' },
      { path: '/notifications', labelKey: 'nav.notifications', resource: 'system.notification', action: 'read', icon: 'bell', screen: 'notifications' },
      // Входящая почта (#121): письма, которые не удалось сопоставить с карточкой. Своё право
      // mail.inbox — общий ящик разбирает руководитель (all), менеджер видит только свой личный.
      { path: '/mail/inbox', labelKey: 'nav.mailInbox', resource: 'mail.inbox', action: 'read', icon: 'inbox', screen: 'mail-inbox' },
      // Мессенджеры (#125): чаты Telegram и WhatsApp, собеседника которых система не узнала.
      // Своё право messenger.inbox — бот и номер общие, разбирает руководитель продаж (all).
      { path: '/messenger/inbox', labelKey: 'nav.messengerInbox', resource: 'messenger.inbox', action: 'read', icon: 'comment', screen: 'messenger-inbox' },
      // Ассистент (#136): вопрос к данным CRM словами. Право — то же, что у остальных функций ИИ;
      // что именно он прочтёт, решают права спрашивающего на каждый вопрос, на сервере.
      { path: '/assistant', labelKey: 'nav.assistant', resource: 'ai.assistant', action: 'use', icon: 'wand', screen: 'assistant' },
    ],
  },

  {
    // Администрирование (#97). Одно право на раздел — `system.settings`: маска счёта,
    // порог скидки и часовой пояс меняют поведение системы для всех, и выдать это
    // каждому, кто правит счета, значит отдать нумерацию одному менеджеру. Пользователи и
    // роли — отдельные права (`system.user`, `system.role`, #102).
    titleKey: 'nav.section.admin',
    items: [
      // Порядок — как в Sidebar.dc.html (вторая партия дизайна): доступ, журнал, настройки,
      // затем справочники продаж и общие справочники. Права у пунктов прежние: доступ —
      // system.user и system.role (#102), журнал — system.audit (#104), остальное — system.settings.
      { path: '/admin/users', labelKey: 'nav.adminUsers', resource: 'system.user', action: 'read', icon: 'users', screen: 'admin-users' },
      { path: '/admin/roles', labelKey: 'nav.adminRoles', resource: 'system.role', action: 'read', icon: 'shield', screen: 'admin-roles' },
      { path: '/admin/audit', labelKey: 'nav.adminAudit', resource: 'system.audit', action: 'read', icon: 'book', screen: 'admin-audit' },
      { path: '/admin/settings', labelKey: 'nav.adminSettings', resource: 'system.settings', action: 'read', icon: 'settings', screen: 'admin-settings' },
      { path: '/admin/numbering', labelKey: 'nav.adminNumbering', resource: 'system.settings', action: 'read', icon: 'hash', screen: 'admin-numbering' },
      { path: '/admin/thresholds', labelKey: 'nav.adminThresholds', resource: 'system.settings', action: 'read', icon: 'stamp', screen: 'admin-thresholds' },
      { path: '/admin/funnels', labelKey: 'nav.adminFunnels', resource: 'system.settings', action: 'read', icon: 'filter', screen: 'admin-funnels' },
      { path: '/admin/lead-rules', labelKey: 'nav.adminLeadRules', resource: 'system.settings', action: 'read', icon: 'user-plus', screen: 'admin-lead-rules' },
      // Маршрутизация обращений по разбору ИИ (#132): то же право раздела, что у распределения лидов.
      { path: '/admin/support-routing', labelKey: 'nav.adminSupportRouting', resource: 'system.settings', action: 'read', icon: 'user-plus', screen: 'admin-support-routing' },
      // Автосценарии (#116): конструктор правил и журнал запусков — то же право раздела, что у
      // распределения лидов: правило действует на все записи сущности, для всех.
      { path: '/admin/automation', labelKey: 'nav.adminAutomation', resource: 'system.settings', action: 'read', icon: 'wand', screen: 'admin-automation' },
      { path: '/admin/deal-reasons', labelKey: 'nav.adminDealReasons', resource: 'system.settings', action: 'read', icon: 'flag', screen: 'admin-deal-reasons' },
      { path: '/admin/tags', labelKey: 'nav.adminTags', resource: 'system.settings', action: 'read', icon: 'flag', screen: 'admin-tags' },
      { path: '/admin/custom-fields', labelKey: 'nav.adminCustomFields', resource: 'system.settings', action: 'read', icon: 'wand', screen: 'admin-custom-fields' },
      { path: '/admin/currencies', labelKey: 'nav.adminCurrencies', resource: 'system.settings', action: 'read', icon: 'coins', screen: 'admin-currencies' },
      { path: '/admin/tax-rates', labelKey: 'nav.adminTaxRates', resource: 'system.settings', action: 'read', icon: 'percent', screen: 'admin-tax-rates' },
      { path: '/admin/units', labelKey: 'nav.adminUnits', resource: 'system.settings', action: 'read', icon: 'ruler', screen: 'admin-units' },
      // Почта (#117): шаблоны писем — справочник раздела; подключение SMTP на том же экране
      // закрыто своим правом system.integration.
      { path: '/admin/mail', labelKey: 'nav.adminMail', resource: 'system.settings', action: 'read', icon: 'mail', screen: 'admin-mail' },
      // ИИ (#126): реестр промптов — справочник раздела; подключение провайдера, включение на
      // организацию и расход на том же экране закрыты своим правом system.integration.
      { path: '/admin/ai', labelKey: 'nav.adminAi', resource: 'system.settings', action: 'read', icon: 'wand', screen: 'admin-ai' },
      // Телефония (#122): ключ и соль АТС — учётные данные, как SMTP и провайдер ИИ, поэтому
      // весь экран под system.integration, а не под справочниками раздела.
      { path: '/admin/telephony', labelKey: 'nav.adminTelephony', resource: 'system.integration', action: 'read', icon: 'phone', screen: 'admin-telephony' },
      // Мессенджеры (#125): токен бота и токен Meta — учётные данные, как у АТС.
      { path: '/admin/messengers', labelKey: 'nav.adminMessengers', resource: 'system.integration', action: 'read', icon: 'comment', screen: 'admin-messengers' },
      // Интеграции (#124): сводка всех подключений, push-каналы и исходящие вебхуки — экран
      // целиком под system.integration: он говорит, где лежат учётные данные, и решает, какие
      // события компании уходят наружу.
      { path: '/admin/integrations', labelKey: 'nav.adminIntegrations', resource: 'system.integration', action: 'read', icon: 'link', screen: 'admin-integrations' },
    ],
  },
]

/**
 * Оставляет только доступные пункты.
 *
 * Раздел без единого доступного пункта исчезает целиком: заголовок «Кадры» над пустотой
 * сообщает ровно то, что мы прячем, — что раздел существует и закрыт. Пустая ветка хуже
 * отсутствующей ещё и тем, что по ней кликают.
 */
export function visibleNavigation(user: CurrentUser | null): NavSection[] {
  return NAVIGATION.map((section) => ({
    ...section,
    items: section.items.filter((item) => allowed(user, item)),
  })).filter((section) => section.items.length > 0)
}

/** Плоский список доступных пунктов — для палитры команд и горячих клавиш. */
export function visibleItems(user: CurrentUser | null): NavItem[] {
  return visibleNavigation(user).flatMap((section) => section.items)
}

/**
 * Доступен ли путь.
 *
 * Роутер спрашивает это перед отрисовкой: переход по прямой ссылке минует меню, и без
 * проверки пользователь увидит экран, пункта которого у него нет. Данные он всё равно не
 * получит — сервер откажет, — но экран с бесконечной загрузкой и 403 в консоли выглядит
 * как поломка, а не как запрет.
 */
export function canOpenPath(user: CurrentUser | null, path: string): boolean {
  const item = [...TOP_ITEMS, ...NAVIGATION.flatMap((section) => section.items)]
    .find((it) => it.path === path)
  // Путь вне навигации — служебный (профиль, настройки), и права раздела к нему
  // неприменимы. Отказывать по умолчанию значило бы закрыть их всем.
  if (!item) return true
  return allowed(user, item)
}

/**
 * Куда вести с корня.
 *
 * «Мой день», если он положен: экран отвечает на вопрос «что мне сделать сейчас», и после
 * входа человек должен попадать туда, а не в первый раздел учёта. Иначе — первый
 * доступный раздел, а не фиксированная главная: у кладовщика нет прав на сделки, и жёсткий
 * редирект на `/deals` встретил бы его отказом при каждом входе.
 */
export function startPath(user: CurrentUser | null): string {
  const first = [...TOP_ITEMS, ...NAVIGATION.flatMap((section) => section.items)].find((item) =>
    canOpenPath(user, item.path),
  )
  return first?.path ?? '/no-access'
}
